import { useCallback, useEffect, useMemo, useState } from "react";
import CheckmateApiClient from "../../services/apiClient";
import { ICaseModel } from "../../interfaces/ICase";
import { ICaseResolutionModel } from "../../interfaces/ICaseResolutionModel";

const _apiClient = new CheckmateApiClient();

type ICaseResolutionCaseFileSectionProps = {
    caseGuid?: string;
    currentCase?: ICaseModel;
    readonly?: boolean;
    refreshParent?: () => void;
};

enum CaseResolutionStatusEnum {
    Inactive = "Inactive",
    Enrolled = "Enrolled",
    Excluded = "Excluded",
}

export const CaseResolutionCaseFileSection = ({ currentCase, refreshParent }: ICaseResolutionCaseFileSectionProps) => {
    const [caseResolutionModel, setCaseResolutionModel] = useState<ICaseResolutionModel>();
    const [caseResolutionStatus, setCaseResolutionStatus] = useState<string>(CaseResolutionStatusEnum.Inactive);
    const [busy, setBusy] = useState<boolean>(false);

    useEffect(() => {
        if (!currentCase || !currentCase.caseAssertions) {
            setCaseResolutionStatus(CaseResolutionStatusEnum.Inactive);
            return;
        }

        const statusAssertions = currentCase.caseAssertions.filter(ca => ca.assertionDescriptorGuid?.toLocaleLowerCase() === '04e41863-a3fc-4088-aca7-a81d746ec2af');
        if (statusAssertions.length === 0) {
            setCaseResolutionStatus(CaseResolutionStatusEnum.Inactive);
            return;
        }
        const statusAssertion = statusAssertions[0];
        if (!statusAssertion.assertionDescriptorValueGuid) {
            setCaseResolutionStatus(CaseResolutionStatusEnum.Inactive);
            return;
        }
        const caseResolutionStatusGuidToLabelMap: { [key: string]: CaseResolutionStatusEnum } = {
            'bbf04882-4886-4d5a-a850-868606a98b1f': CaseResolutionStatusEnum.Inactive,
            '650de667-bee8-4977-8825-ff1593b1d9e7': CaseResolutionStatusEnum.Enrolled,
            'aaafd6b7-f079-4860-a3a8-514e71f97012': CaseResolutionStatusEnum.Excluded,
        };
        setCaseResolutionStatus(caseResolutionStatusGuidToLabelMap[statusAssertion.assertionDescriptorValueGuid.toLocaleLowerCase()] ?? CaseResolutionStatusEnum.Inactive);
    }, [currentCase, caseResolutionModel]);

    const refreshCaseResolutionStatus = async () => {
        if (!currentCase || !currentCase.guid) {
            return;
        } 
        const response = await _apiClient.getCaseResolutionCase(currentCase!.guid!, true);
        if (!response.httpResponse.ok || !response.payload) {
            return;
        }
        setCaseResolutionModel(response.payload);
    };

    useEffect(() => {
        refreshCaseResolutionStatus();
    }, [currentCase]);

    const activeStep = useMemo(() => {
        if (!caseResolutionModel || !caseResolutionModel.steps) {
            return undefined;
        }
        return caseResolutionModel.steps.find(step => step.isActive);
    }, [caseResolutionModel]);

    const handleEnrollClick = useCallback(async () => {
        setBusy(true);
        try {
            if (!currentCase || !currentCase.guid) {
                return;
            }
            await _apiClient.createCaseResolutionCasePlan({
                CaseId: currentCase!.guid!
            });
            if (refreshParent) {
                refreshParent();
            }
        }
        finally {
            setBusy(false);
        }
    }, [currentCase, refreshParent]);

    return (
        <div style={{ paddingBottom: '1em' }}>
            <em>Status: {caseResolutionStatus ?? CaseResolutionStatusEnum.Inactive}</em>
            {caseResolutionStatus === CaseResolutionStatusEnum.Enrolled ? <>
                <br />
                {activeStep ? `Current Step: ${activeStep.name}` : 'No Active Step'}
                <br />
                <a target="_blank" href={`${caseResolutionModel?.caseResolutionUrl}`}>Go to Case Resolution</a>
            </> : <>
                <br />
                <button className="btn btn-default" onClick={handleEnrollClick} disabled={busy}>{busy ? 'Enrolling...' : 'Enroll in Case Resolution'}</button>
            </>}
        </div>
    )
}